import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as AlarmClock } from '../../images/icons/alarm-clock-regular.svg';

interface QuestionTimerProps {
    timeLimit: number;
    onComplete?: () => void;
    inOverlay?: boolean;
    isActive?: boolean;
}

const Timer: React.FC<QuestionTimerProps> = ({ timeLimit, onComplete, inOverlay = false, isActive = true }) => {
    const [progress, setProgress] = useState(100);
    const [timeLeft, setTimeLeft] = useState(timeLimit);
    const [color, setColor] = useState('#4CAF50');
    const [isShaking, setIsShaking] = useState(false);
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    const hasCompletedRef = useRef(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const radius = 50;
    const stroke = 4;
    const normalizedRadius = radius - stroke * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = -1 * (circumference - (progress / 100) * circumference);

    useEffect(() => {
        if (!isActive || hasCompletedRef.current) return;
        const deadline = Date.now() + timeLimit * 1000;
        intervalRef.current = setInterval(() => {
            const remaining = Math.max(0, (deadline - Date.now()) / 1000);
            setTimeLeft(remaining);
            setProgress((remaining / timeLimit) * 100);
            if (remaining <= timeLimit * 0.6) setColor('#FFA500');
            if (remaining <= timeLimit * 0.3) {
                setColor('#FF0000');
                setIsShaking(true);
            }
            if (remaining === 0 && !hasCompletedRef.current) {
                hasCompletedRef.current = true;
                clearInterval(intervalRef.current!);
                setIsShaking(false);
                onCompleteRef.current?.();
            }
        }, 100);

        return () => clearInterval(intervalRef.current!);
    }, [isActive, timeLimit]);

    return (
        <div
            className={`timer-container ${inOverlay ? '' : 'no-overlay'}`}
            role="timer" aria-label={`${Math.ceil(timeLeft)} s`}
        >
            <svg height={radius * 2} width={radius * 2} className="timer-circle">
                <circle stroke="#ddd" fill="transparent" strokeWidth={stroke} r={normalizedRadius} cx={radius} cy={radius} />
                <circle
                    stroke={color}
                    fill="transparent"
                    strokeWidth={stroke}
                    strokeDasharray={`${circumference} ${circumference}`}
                    style={{ strokeDashoffset }}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                    transform={`rotate(-90 ${radius} ${radius})`}
                />
            </svg>
            <div className="timer-wrapper">
                {timeLeft === 0 ? <span className="timer-expired">0 s</span> : <AlarmClock className={`timer-icon ${isShaking ? 'shake' : ''}`} />}
            </div>
        </div>
    );
};

export default React.memo(Timer);
